export type Suggestion = { value: string; label: string; kind: "user" | "group" | "page" | "new" | "skein"; id?: string };

const DELAY_MS = 150;
let counter = 0;

// The "@na" or "#pl" being typed just before the caret, in a textarea.
const MENTION_AT_CARET = /(?:^|[^\p{L}\p{N}_.@#/-])([@#][\p{L}\p{N}_-]{0,40})$/u;

/**
 * Wraps an <input> and suggests "@user" / "#group" values from the lookup endpoint.
 * Wrapping a <textarea> instead completes mentions as they are typed anywhere in the text.
 */
export class P9Autocomplete extends HTMLElement {
  #input!: HTMLInputElement | HTMLTextAreaElement;
  #inText = false;
  #list!: HTMLUListElement;
  #items: Suggestion[] = [];
  #active = -1;
  #timer: number | undefined;
  #sequence = 0;

  connectedCallback(): void {
    const input = this.querySelector<HTMLInputElement | HTMLTextAreaElement>("input, textarea");
    if (!input || !this.dataset.url) return;
    this.#input = input;
    // A textarea, or an input marked data-multi (several "@who #group" names), completes the name at the caret.
    this.#inText = input instanceof HTMLTextAreaElement || this.dataset.multi !== undefined;

    const id = `p9-ac-${++counter}`;
    this.#list = document.createElement("ul");
    this.#list.id = id;
    this.#list.className = "autocomplete-list";
    this.#list.setAttribute("role", "listbox");
    this.#list.hidden = true;
    this.append(this.#list);

    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-controls", id);
    input.setAttribute("aria-expanded", "false");

    input.addEventListener("input", () => {
      window.clearTimeout(this.#timer);
      this.#timer = window.setTimeout(() => void this.#fetch(), DELAY_MS);
    });
    input.addEventListener("focus", () => {
      if (!this.#inText && input.value.trim()) void this.#fetch();
    });
    input.addEventListener("keydown", (event) => this.#onKey(event as KeyboardEvent));
    input.addEventListener("blur", () => window.setTimeout(() => this.#close(), 120));
    this.#list.addEventListener("mousedown", (event) => {
      const option = (event.target as HTMLElement).closest<HTMLLIElement>("li[data-index]");
      if (!option) return;
      event.preventDefault();
      this.#choose(+option.dataset.index!);
    });
  }

  /** What is being completed: the whole value, or the mention before the caret (null when there is none). */
  #query(): { start: number; text: string } | null {
    if (!this.#inText) return { start: 0, text: this.#input.value };
    const caret = this.#input.selectionStart ?? 0;
    if (caret !== this.#input.selectionEnd) return null;
    const match = MENTION_AT_CARET.exec(this.#input.value.slice(0, caret));
    return match ? { start: caret - match[1]!.length, text: match[1]! } : null;
  }

  async #fetch(): Promise<void> {
    const sequence = ++this.#sequence;
    const query = this.#query();
    if (!query) {
      this.#close();
      return;
    }
    const url = new URL(this.dataset.url!, window.location.href);
    url.searchParams.set("q", query.text);
    if (this.dataset.kinds) url.searchParams.set("kinds", this.dataset.kinds);
    const response = await fetch(url, { headers: { Accept: "application/json" } });
    if (sequence !== this.#sequence || !response.ok) return;
    this.#items = (await response.json()) as Suggestion[];
    // data-create: also offer what was typed as a page that doesn't exist yet (a red link), unless a page has that title.
    const typed = query.text.trim().replace(/\s+/g, " ");
    if (this.dataset.create !== undefined && typed && !this.#items.some((i) => i.value.toLowerCase() === typed.toLowerCase()))
      this.#items.push({ value: typed, label: "New page (a red link until someone creates it)", kind: "new" });
    this.#render();
  }

  #render(): void {
    this.#active = this.#items.length ? 0 : -1;
    this.#list.replaceChildren(
      ...this.#items.map((item, i) => {
        const li = document.createElement("li");
        li.id = `${this.#list.id}-${i}`;
        li.dataset.index = String(i);
        li.setAttribute("role", "option");
        const value = document.createElement("span");
        value.className = "ac-value";
        value.textContent = item.value;
        const label = document.createElement("span");
        label.className = "ac-label";
        label.textContent = item.kind === "group" ? `${item.label} (group)` : item.label;
        if (!item.label) label.hidden = true;
        li.append(value, label);
        return li;
      }),
    );
    const open = this.#items.length > 0;
    this.#list.hidden = !open;
    this.#input.setAttribute("aria-expanded", String(open));
    this.#highlight();
  }

  #highlight(): void {
    [...this.#list.children].forEach((li, i) => li.setAttribute("aria-selected", String(i === this.#active)));
    if (this.#active >= 0) this.#input.setAttribute("aria-activedescendant", `${this.#list.id}-${this.#active}`);
    else this.#input.removeAttribute("aria-activedescendant");
  }

  #onKey(event: KeyboardEvent): void {
    if (this.#list.hidden) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      this.#active = (this.#active + step + this.#items.length) % this.#items.length;
      this.#highlight();
    } else if (event.key === "Enter" && this.#active >= 0) {
      event.preventDefault();
      this.#choose(this.#active);
    } else if (event.key === "Tab" && !event.shiftKey && this.#active >= 0 && this.#query()?.text !== this.#items[this.#active]?.value) {
      // Shell-style completion; once the value matches, Tab moves on as usual.
      event.preventDefault();
      this.#choose(this.#active);
    } else if (event.key === "Escape") {
      this.#close();
    }
  }

  #choose(index: number): void {
    const item = this.#items[index];
    if (!item) return;
    const query = this.#query();
    if (this.#inText && query) {
      const caret = query.start + query.text.length;
      const value = this.#input.value;
      this.#input.value = value.slice(0, query.start) + item.value + " " + value.slice(caret);
      const after = query.start + item.value.length + 1;
      this.#input.setSelectionRange(after, after);
    } else if (!this.#inText) {
      this.#input.value = item.value;
    }
    this.#close();
    this.dispatchEvent(new CustomEvent<Suggestion>("p9-select", { detail: item, bubbles: true }));
    this.#input.focus();
  }

  #close(): void {
    this.#list.hidden = true;
    this.#input.setAttribute("aria-expanded", "false");
    this.#input.removeAttribute("aria-activedescendant");
  }
}

customElements.define("p9-autocomplete", P9Autocomplete);
